import { Visibility } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, LinearProgress, Modal, Stack, Tooltip, Typography } from '@mui/material';
import { DataGridPremium, GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useMemo, useState } from 'react';
import { SectionHeader } from 'shared/components/massActions';
import { useColumns } from 'shared/hooks';
import { GridColumns, ServerSideProps } from 'shared/types';
import {
    formatDatePickerStringWithoutTime,
    formatFourDecimalMoney,
    formatUtcDateTimeToLocalDateTimeString,
    parseDatePickerDate,
    parseUtcDateTime,
} from 'shared/utility';
import { getStatusColumnColor, moneyFourDecimalFormatter } from 'shared/utility/dataGrid';
import { PriceHistoryData } from 'types/backend';

interface PriceHistoryModalProps {
    open: boolean;
    isLoading: boolean;
    isFetching: boolean;
    rows: PriceHistoryData[];
    onClose: () => void;
    serverSideProps: ServerSideProps;
    totalRows: number;
    sortModel?: GridSortModel;
    onSortModelChange?: (newSortModel: GridSortModel) => void;
}

export type PriceHistoryDialogProps<T> = {
    open: boolean;
    row?: T;
};

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '10%',
    left: '10%',
    width: '80%',
    bgcolor: 'background.paper',
    border: '2px solid #CCC',
    boxSizing: 'border-box',
    boxShadow: 24,
    p: 0,
    m: 0,
};

export function PriceHistoryModal({
    open,
    isLoading,
    isFetching,
    rows,
    onClose,
    serverSideProps,
    totalRows,
    sortModel,
    onSortModelChange
}: PriceHistoryModalProps) {
    const [filterButtonEl] = useState<HTMLButtonElement | null>(null);

    const { sortModel: serverSortModel, onSortModelChange: serverOnSortModelChange, ...restServerSideProps } =
        serverSideProps;

    const columnConfig: GridColumns<PriceHistoryData> = [
        {
            headerName: 'id',
            field: 'id',
            hide: true,
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Last Updated',
            field: 'effectivestart',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
            type: 'dateTime',
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
        },
        {
            headerName: 'Modified By',
            field: 'modifiedBy',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Product',
            field: 'productId',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Price Type',
            field: 'priceType',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'OTO',
            field: 'isOneTimeOnly',
            hideable: false,
            pinnable: false,
            filterable: false,
            type: 'boolean',
            width: 150,
            renderCell: (params) => (params.row.priceType === 'Deviation' ? (params.value ? 'Y' : 'N') : ''),
        },
        {
            headerName: 'Price',
            field: 'price',
            hideable: false,
            pinnable: false,
            filterable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 100,
        },
        {
            headerName: 'Price per Unit',
            field: 'poundsPerPallet',
            hideable: false,
            pinnable: false,
            filterable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 200,
            renderCell: (params) =>
                formatFourDecimalMoney(params.row.price * (params.row.poundsPerPallet / params.row.unitsPerPallet)),
        },
        {
            headerName: 'Target Price',
            field: 'targetPrice',
            hideable: false,
            pinnable: false,
            filterable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 100,
        },
        {
            headerName: 'Historical Entry Expiration',
            field: 'effectiveend',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
            type: 'dateTime',
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
        },
        {
            headerName: 'Start Date',
            field: 'startdate',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
            type: 'date',
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
        },
        {
            headerName: 'End Date',
            field: 'enddate',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
            type: 'date',
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
        },
        {
            headerName: 'Reason',
            field: 'reason',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Status',
            field: 'status',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
            renderCell: (params) => (
                <Chip label={params.value} color={getStatusColumnColor(params.value)} variant='outlined' />
            ),
        },
        {
            headerName: 'Approver',
            field: 'approver',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Comments',
            field: 'comments',
            hideable: false,
            pinnable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            width: 120,
            renderCell: (params) =>
                params.value ? (
                    <Tooltip title={<span style={{ fontSize: '.85rem' }}>{params.value}</span>}>
                        <Visibility />
                    </Tooltip>
                ) : (
                    <Visibility style={{ color: '#CCC' }} />
                ),
        },
    ];

    const columns = useColumns<PriceHistoryData>({ columnConfig });

    const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(
        () =>
            columns.reduce<GridColumnVisibilityModel>((model, column) => {
                if (column.hide) model[column.field] = false;
                return model;
            }, {}),
        [columns]
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-describedby='modal-price-history-description'
            aria-labelledby='modal-price-history-title'
        >
            <Stack sx={modalStyle}>
                <SectionHeader>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: 6,
                            p: 1,
                        }}
                    >
                        <Typography variant={'h5'}>Price History</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon sx={{ color: 'white' }} />
                        </IconButton>
                    </Box>
                </SectionHeader>
                <Stack>
                    <Stack sx={{ p: 2, flexDirection: 'row', flexWrap: 'wrap' }}>
                        <DataGridPremium
                            autoHeight
                            getRowId={(row) => row.id}
                            columns={columns}
                            rows={rows}
                            initialState={{ columns: { columnVisibilityModel } }}
                            paginationMode='server'
                            pageSizeOptions={[15, 25, 50, 100]}
                            pagination
                            sortingMode='server'
                            sortModel={sortModel}
                            onSortModelChange={onSortModelChange}
                            {...restServerSideProps}
                            loading={isLoading || isFetching}
                            rowCount={totalRows}
                            paginationModel={{ page: restServerSideProps.paginationModel?.page ?? 0, pageSize: 100 }}
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-virtualScroller': {
                                    overflowY: 'scroll !important',
                                    minHeight: '55vh',
                                    maxHeight: '55vh',
                                    border: '1px solid #DDDDDD',
                                    borderTop: 'none',
                                },
                                '& .MuiDataGrid-filterFormColumnInput': {
                                    width: 'auto',
                                },
                                '& .MuiDataGrid-toolbarContainer': {
                                    padding: '5px',
                                    marginBottom: '10px',
                                },
                                '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-pinnedColumnHeaders': {
                                    backgroundColor: '#f1f1f1',
                                    border: '1px solid #DDDDDD',
                                    borderBottom: 'none',
                                    borderRadius: '0',
                                },
                                '& .MuiButton-root, & .MuiIconButton-root, .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 'bold',
                                },
                            }}
                            components={{
                                NoRowsOverlay: () => (
                                    <Stack height='100%' alignItems='center' justifyContent='center'>
                                        No Results
                                    </Stack>
                                ),
                                LoadingOverlay: LinearProgress,
                            }}
                            componentsProps={{
                                panel: {
                                    anchorEl: filterButtonEl,
                                    sx: {
                                        '& .MuiDataGrid-filterFormColumnInput': {
                                            width: 'auto',
                                        },
                                        '& .MuiDataGrid-toolbarContainer': {
                                            padding: '5px',
                                            marginBottom: '10px',
                                        },
                                    },
                                },
                            }}
                        />
                    </Stack>
                </Stack>
            </Stack>
        </Modal>
    );
}
